import React from 'react';
import type { FormLayoutNode } from '../types/layout';
import { FormSectionRenderer } from './layout/SectionRenderer';
import { FormGridRenderer } from './layout/GridRenderer';
import { FormTabsRenderer } from './layout/TabsRenderer';
import { FormDividerRenderer } from './layout/DividerRenderer';
import { FormStackRenderer } from './layout/StackRenderer';
import { FormSurfaceRenderer } from './layout/SurfaceRenderer';
import { FormFieldsRenderer } from './fields/FormFieldsRenderer';
import { FormEachRenderer } from './fields/FormEachRenderer';
import { FormConditionalRenderer } from './logic/FormConditionalRenderer';
import { FormCustomNodeRenderer } from './escape/FormCustomNodeRenderer';
import { FormActionButtonRenderer } from './escape/FormActionButtonRenderer';
import { FormUdfRenderer } from './fields/FormUdfRenderer';
import { FormScrollAgreementNodeRenderer } from './FormScrollAgreementRenderer';
import { FormHtmlTemplateRenderer } from './FormHtmlTemplateRenderer';
import { FormProseRenderer } from './FormProseRenderer';
import { FormBatchTableRenderer } from './FormBatchTableRenderer';
import { FormEmbeddedTableRenderer } from './FormEmbeddedTableRenderer';
import { FormFileListRenderer } from './view/FormFileListRenderer';
import { FormDataNodeRenderer } from './view/FormDataNodeRenderer';
import { FormColumnGroupRenderer } from './layout/ColumnGroupRenderer';

export interface FormNodeRendererProps {
  node: FormLayoutNode;
  namePrefix?: string;
}

export const FormNodeRenderer = React.memo(function FormNodeRenderer({ node, namePrefix }: FormNodeRendererProps): React.ReactElement | null {
  switch (node.type) {
    case 'section':
      return <FormSectionRenderer node={node} namePrefix={namePrefix} />;
    case 'grid':
      return <FormGridRenderer node={node} namePrefix={namePrefix} />;
    case 'tabs':
      return <FormTabsRenderer node={node} namePrefix={namePrefix} />;
    case 'divider':
      return <FormDividerRenderer />;
    case 'stack':
      return <FormStackRenderer node={node} namePrefix={namePrefix} />;
    case 'surface':
      return <FormSurfaceRenderer node={node} namePrefix={namePrefix} />;
    case 'fields':
      return <FormFieldsRenderer node={node} namePrefix={namePrefix} />;
    case 'each':
      return <FormEachRenderer node={node} />;
    case 'conditional':
      return <FormConditionalRenderer node={node} namePrefix={namePrefix} />;
    case 'custom':
      return <FormCustomNodeRenderer node={node} />;
    case 'action-button':
      return <FormActionButtonRenderer node={node} />;
    case 'udf':
      return <FormUdfRenderer node={node} namePrefix={namePrefix} />;
    case 'scroll-agreement':
      return <FormScrollAgreementNodeRenderer node={node} namePrefix={namePrefix} />;
    case 'html-template':
      return <FormHtmlTemplateRenderer node={node} />;
    case 'prose':
      return <FormProseRenderer node={node} />;
    case 'batch-table':
      return <FormBatchTableRenderer node={node} namePrefix={namePrefix} />;
    case 'embedded-table':
      return <FormEmbeddedTableRenderer node={node} />;
    case 'file-list':
      return <FormFileListRenderer node={node} />;
    case 'data':
      return <FormDataNodeRenderer node={node} namePrefix={namePrefix} />;
    case 'column-group':
      return <FormColumnGroupRenderer node={node} namePrefix={namePrefix} />;
    default: {
      const _exhaustive: never = node;
      return null;
    }
  }
});
